import { ROUTE } from '@/config';
import { generatePath } from 'react-router-dom';

export enum TicketFormsEnum {
  ORDER = 17707453320002,
  DELIVERY = 17707471820004,
  PAYMENT = 17707486230007,
  APP = 17707491960005,
}

export enum TicketEntityIDFieldEnum {
  ORDER = 10219810140, // order platform code
  PICKUP = 11067722935, // pickup platform code
  REMITTANCE = 13511939364, // remittance platform code
}

// export const TicketEntityUIDField = 30148215813917; // for all entities
// export const TicketStatusField = 28578091769117; //for all forms
// export const TicketSolvedStatus = 28578057836445; //for all forms
export const TicketDescriptionField = 28578091767965; //for all forms

/**
 * The four issue dropdowns, keyed by the ticketing backend's field id.
 *
 * 🔴 THE NAMES USED TO BE WRONG, AND TWO WERE CROSSED. Verified against
 * PROD `GET /api/v1/external/categories` and the backend's own id derivation
 * (`FormMappingService::generateFieldId` = crc32('field_' + key)):
 *
 *   10025206674 = order_issues          (was ORDER_ISSUES   — correct)
 *   13656737237 = payment_issue         (was DELIVERY_ISSUES — WRONG)
 *   10356134903 = pickups_rtos_issues   (was PAYMENT_ISSUES  — WRONG)
 *   12909112969 = general_inquiries     (was APP_ISSUES      — WRONG)
 *
 * ⭐ There is no "delivery" field and no "app" field in the backend at all.
 * "I need help with using the app" is an OPTION inside general_inquiries, which
 * is most likely where APP came from. Renamed to the backend's own vocabulary
 * so the next reader cannot repeat the mistake — a name in our code is not a
 * fact about another service.
 */
export enum TicketIssueFieldEnum {
  ORDER_ISSUES = 10025206674,
  PAYMENT_ISSUES = 13656737237,
  PICKUP_RTO_ISSUES = 10356134903,
  GENERAL_INQUIRIES = 12909112969,
}

export enum TicketOtherIssueFieldIDEnum {
  ORDER = 1002520667413,
  DELIVERY = 1035613490302,
  PAYMENT = 1365673723705,
  APP = 1290911296908,
}

export const formByEntityFieldId = {
  [TicketEntityIDFieldEnum.ORDER]: { formId: TicketFormsEnum.ORDER },
  [TicketEntityIDFieldEnum.PICKUP]: { formId: TicketFormsEnum.DELIVERY },
  [TicketEntityIDFieldEnum.REMITTANCE]: { formId: TicketFormsEnum.PAYMENT },
};

export const entityFieldList = [
  TicketEntityIDFieldEnum.ORDER,
  TicketEntityIDFieldEnum.PICKUP,
  TicketEntityIDFieldEnum.REMITTANCE,
];

// Used only as a membership test ("is this field one of the four issue
// dropdowns?"), so it was correct even while the names above were not —
// which is exactly why the mislabelling survived.
export const issueFieldList = [
  TicketIssueFieldEnum.ORDER_ISSUES,
  TicketIssueFieldEnum.PAYMENT_ISSUES,
  TicketIssueFieldEnum.PICKUP_RTO_ISSUES,
  TicketIssueFieldEnum.GENERAL_INQUIRIES,
];
export const otherIssueFieldByForm = {
  [TicketFormsEnum.ORDER]: TicketOtherIssueFieldIDEnum.ORDER,
  [TicketFormsEnum.DELIVERY]: TicketOtherIssueFieldIDEnum.DELIVERY,
  [TicketFormsEnum.PAYMENT]: TicketOtherIssueFieldIDEnum.PAYMENT,
  [TicketFormsEnum.APP]: TicketOtherIssueFieldIDEnum.APP,
};

// 🔴 `issueFieldByForm` DELETED — it was exported, imported by nothing, and
// WRONG for two of its four entries: it paired the PAYMENT form with the
// pickup/RTO field and the pickup/RTO form with the payment field, because it
// was built by matching the labels above rather than the ids beneath them.
//
// ⭐⭐ A dead export with a plausible name is a loaded gun. Nothing was broken
// only because nobody had reached for it yet, and the per-comment
// classification work is about to touch exactly this surface. `formByEntityFieldId`
// and `otherIssueFieldByForm` below were both verified correct and are kept.

export const TICKET_ENTITY_CONFIG: Record<
  string,
  (uid?: string) => {
    singlePageUrl?: string;
    entityName: string;
    entityId: TicketEntityIDFieldEnum;
  }
> = {
  BXO: (uid) => ({
    singlePageUrl: uid ? `${ROUTE.ORDER_DETAILS}/${uid}` : undefined,
    entityId: TicketEntityIDFieldEnum.ORDER,
    entityName: 'order',
  }),
  BXPU: (uid) => ({
    singlePageUrl: uid ? `${ROUTE.PICKUPS_DETAILS}/${uid}` : undefined,
    entityId: TicketEntityIDFieldEnum.PICKUP,
    entityName: 'pickup',
  }),
  BXRTO: (uid) => ({
    singlePageUrl: uid ? generatePath(ROUTE.PICKUP_RTO_DETAILS, { id: uid }) : undefined,
    entityId: TicketEntityIDFieldEnum.PICKUP,
    entityName: 'return',
  }),
  BXRE: (uid) => ({
    singlePageUrl: uid ? generatePath(ROUTE.FINANCE_DETAILS, { id: uid }) : undefined,
    entityId: TicketEntityIDFieldEnum.REMITTANCE,
    entityName: 'remittance',
  }),
};
